// lib imports
import * as Hammer from 'hammerjs';
import * as React from 'react';

// Core imports.
import StampModel from 'unspoken-core/lib/models/Stamp';
import Stimulus, { SwipeDirection } from 'unspoken-core/lib/models/Stimulus';

// local imports
import { DragStartPosition } from '../enums/DragStartPosition';
import { StampPosition } from '../enums/StampPosition';
import { Position } from '../models/Position';
import { Size } from '../models/Size';
import ResetOverlay from './ResetOverlay';
import Stamp from './Stamp';

// Constants, if you update these durations, they should be updated in the CSS as well
const STARTING_ANIMATION_DURATION = 400; // milliseconds
const RETURNING_DURATION_DURATION = 400; // milliseconds
const FINISHING_ANIMATION_DURATION = 400; // milliseconds

enum StimulusState {
  HIDDEN = 'HIDDEN',
  STARTING = 'STARTING',
  IDLE = 'IDLE',
  RESET = 'RESET',
  SWIPING = 'SWIPING',
  RETURNING = 'RETURNING',
  FINISHING = 'FINISHING',
}

// Component props
interface AttractionMobileStimulusProps {
  active: boolean;
  index: number;
  leftStampOptions: StampModel;
  onSwipe: (index: number, direction: SwipeDirection, resets: number, duration: number) => void;
  rightStampOptions: StampModel;
  stimulus: Stimulus;
  timeout: number;
  background: string;
}

// Component state
interface AttractionMobileStimulusState {
  containerSize: Size;
  dragStartPosition: DragStartPosition;
  imagePosition: Position;
  imageLoaded: boolean;
  imageSizeOriginal: Size;
  imageSizeRender: Size;
  resets: number;
  stimulusState: StimulusState;
}

class AttractionMobileStimulus extends React.Component<AttractionMobileStimulusProps, AttractionMobileStimulusState> {

  // store all the timeout Ids in array to prevent race conditions
  public resetTimeoutIds: number[] = [];

  // store the timestamp of the moment the stimulus is shown, 
  // to calculate the duration before respondent swipes left or right
  public startTime: Date = new Date();

  public state: AttractionMobileStimulusState = {
    containerSize: {
      height: 0,
      width: 0,
    },
    dragStartPosition: DragStartPosition.BOTTOM,
    imageLoaded: false,
    imagePosition: {
      x: 0,
      y: 0,
    },
    imageSizeOriginal: {
      height: 0,
      width: 0,
    },
    imageSizeRender: {
      height: 0,
      width: 0,
    },
    resets: 0,
    stimulusState: StimulusState.IDLE,
  };

  // Variables used within the component. 
  private hammer: HammerManager;
  private draggableElement: HTMLDivElement;
  private containerElement: HTMLDivElement;

  // React lifecycle event: Init component
  public componentDidMount() {

    // Init hammer
    this.hammer = new Hammer.Manager(this.draggableElement);
    this.hammer.add(new Hammer.Pan({ threshold: 2 }));
    this.hammer.on('panend', this.panend);
    this.hammer.on('panmove', this.panmove);
    this.hammer.on('panstart', this.panstart);

    // Recalculate render image size on window resize
    window.addEventListener('resize', this.storeImageDimensions);

    // Load stimulus image and fire image load event
    const img = new Image();
    img.onload = () => { this.onImageLoad(img); };
    img.src = this.props.stimulus.url;

    if (!this.props.active) {
      this.setState({stimulusState: StimulusState.HIDDEN});
    }
  }

  // Clear event listeners
  public componentWillUnmount() {
    this.clearResetTimeouts();
    window.removeEventListener('resize', this.storeImageDimensions);
  }

  // Listen to component update, 
  public componentDidUpdate(prevProps: AttractionMobileStimulusProps) {
    // stimulus became active
    if (!prevProps.active && this.props.active && this.state.imageLoaded) {
      // fade in
      this.setState({stimulusState: StimulusState.STARTING}, () => {
        // after fade in, set state to idle
        window.setTimeout(() => { this.setState({stimulusState: StimulusState.IDLE}); }, STARTING_ANIMATION_DURATION );
      });
      this.startResponseTimer();
      this.startResetTimeout();
    }
  }

  // React render method
  public render() {

    // calculate the transition style, based on the swiping state
    let transitionStyle = '';
    if (this.state.stimulusState === StimulusState.RETURNING) {
      transitionStyle = `transform ${RETURNING_DURATION_DURATION}ms linear`;
    } else if (this.state.stimulusState === StimulusState.FINISHING) {
      transitionStyle = `transform ${FINISHING_ANIMATION_DURATION}ms linear`;
    }

    // determine the "transform" CSS properties, based on how far the stimulus is dragged
    const ratioMoved = this.getRatioMoved();
    // if ratio moved positive (swiped right), rotate left (left rotation is negative)
    const rotationDirection = this.state.dragStartPosition === DragStartPosition.BOTTOM ? 1 : -1;
    const rotationDegrees = (ratioMoved * rotationDirection * 90) / 6;
    const transformationStyle = `translate3d(${this.state.imagePosition.x}px, ${this.state.imagePosition.y}px, 0px) rotate(${rotationDegrees}deg)`;

    // combine the styles 
    const draggableStyle = {
      WebkitTransform: transformationStyle,
      msTransform: transformationStyle,
      transform: transformationStyle,
      transition: transitionStyle,
    };

    const opacity = Math.abs(ratioMoved) / .65; // at 65% moved, opacity = 1

    return (
      <>
      
        {/* Render reset overlay if in reset mode */}
        {this.props.active && this.state.stimulusState === StimulusState.RESET && (
          <ResetOverlay onRestartExercise={this.onRestartExercise} />
        )}

        <div
          style={{
            backgroundColor: this.props.background,
            zIndex: (this.props.index * -1) - 1000,   // show stimulus in order (first stimulus must have highest zIndex)
          }}
          className={`attraction-stimulus__container attraction-stimulus__container--state-${this.state.stimulusState.toLowerCase()}`}
          ref={(element) => { if (element) { this.containerElement = element; } }}
        >
          <div
            className="attraction-stimulus__draggable"
            style={{
              height: this.state.imageSizeRender.height - 8,  // because of the way the current CSS is set up, padding doesn't work, so adding padding here
              width: this.state.imageSizeRender.width - 8,
              ...draggableStyle,
            }}
            ref={(element) => { if (element) { this.draggableElement = element; } }}
          >

            <Stamp
              position={StampPosition.LEFT}
              text={this.props.leftStampOptions.text}
              textColor={this.props.leftStampOptions.textColor}
              fadeOut={this.state.stimulusState === StimulusState.RETURNING}
              opacity={ratioMoved < 0 ? opacity : 0}
            />

            <Stamp
              position={StampPosition.RIGHT}
              text={this.props.rightStampOptions.text}
              textColor={this.props.rightStampOptions.textColor}
              fadeOut={this.state.stimulusState === StimulusState.RETURNING}
              opacity={ratioMoved > 0 ? opacity : 0}
            />

            <img 
              onDragStart={(e) => { e.preventDefault(); }}
              draggable={false}
              className="attraction-stimulus__image"
              src={this.props.stimulus.url}
              alt=""
            /> 
          </div>
        </div>
      </>
    );
  }

  // Clears all the reset timeouts
  private clearResetTimeouts = () => {
    this.resetTimeoutIds.forEach(window.clearTimeout);
  }

  // RatioMoved: how far the user has dragged (unit in screen widths)
  private getRatioMoved = () => {
    return this.state.imagePosition.x / (this.state.containerSize.width / 2);
  }

  // Handler for reset overlay button
  private onRestartExercise = () => {
    this.startResponseTimer();
    this.setState({ stimulusState: StimulusState.IDLE }, this.startResetTimeout);
  }

  private onImageLoad = (image: HTMLImageElement) => {
    // Update the cached data
    this.setState({
      imageLoaded: true,
      imageSizeOriginal: {
        height: image.height,
        width: image.width,
      }
    }, () => {
      // Calculcate and store the rendered image dimensions
      this.storeImageDimensions();
      // Then start the exercise timers
      if (this.props.active) {
        this.startResponseTimer();
        this.startResetTimeout();
      }
    });
  }

  // On release drag event
  private panend = (event: HammerInput) => {

    // calc and dispatch move ratio
    const ratioMoved = this.getRatioMoved();

    // event.direction === Hammer.direction
    const swipeDirection = ratioMoved < 0 ? SwipeDirection.LEFT : SwipeDirection.RIGHT;
    const thresholdPassed = (Math.abs(ratioMoved) > 0.75) || Math.abs(event.velocity) > 1.2;
    const movedBackToCenter =
      (swipeDirection === SwipeDirection.LEFT && event.direction === Hammer.DIRECTION_RIGHT) ||
      (swipeDirection === SwipeDirection.RIGHT && event.direction === Hammer.DIRECTION_LEFT);

    // if moved past threshold, let stimulus disappear and dispatch action to parent
    // if moved past threshold, but the last direction was back to center, then don't swipe away
    if (thresholdPassed && !movedBackToCenter) {

      /**
       * Calc the end position:
       * Move the whole image outside the screen
       * = half the screen + image width
       */
      const offset = (this.state.containerSize.width / 2) + (this.state.imageSizeRender.width);
      const newX = ratioMoved < 0 ? -offset : offset; // left or right
      const newY = this.state.imagePosition.y;

      // store the duration until drag released
      const direction = ratioMoved < 0 ? SwipeDirection.LEFT : SwipeDirection.RIGHT;
      const duration = new Date().getTime() - this.startTime.getTime();

      // start the animation
      this.setState({
        imagePosition: { x: newX, y: newY },
        stimulusState: StimulusState.FINISHING,
      });

      // wait for the animation to finish, then dispatch onSwipe
      window.setTimeout(() => {
        this.props.onSwipe(this.props.index, direction, this.state.resets, duration);
      }, FINISHING_ANIMATION_DURATION);

    } else {  
      // move stimulus back to center
      const newPosition = { x: 0, y: 0, };
      this.setState({ stimulusState: StimulusState.RETURNING, imagePosition: newPosition });
      // then start timeout
      window.setTimeout(() => {
        this.setState({ stimulusState: StimulusState.IDLE, imagePosition: newPosition });
        this.startResetTimeout();
      }, RETURNING_DURATION_DURATION);
    }
  }

  // On move drag event
  private panmove = (event: HammerInput) => {
    // Set the current image position to follow drag gesture
    const newPosition = { x: event.deltaX, y: event.deltaY, };
    this.setState({ imagePosition: newPosition });
  }

  // On start drag event
  private panstart = (event: HammerInput) => {
    // store the drag start position to determine the rotation direction
    let dragStartPosition = DragStartPosition.BOTTOM;
    if (event.srcEvent instanceof PointerEvent && (event.srcEvent.layerY > this.state.imageSizeRender.height / 2)) {
      dragStartPosition = DragStartPosition.TOP;
    }
    this.setState({ stimulusState: StimulusState.SWIPING, dragStartPosition });
    // stop the reset timeout
    this.clearResetTimeouts();
  }

  // Set a timeout for the reset screen
  private startResetTimeout = () => {
    // Alway clear the previous timeouts
    this.clearResetTimeouts();
    // Add a new one
    if ( this.props.timeout > 0 ) {
      this.resetTimeoutIds.push(window.setTimeout(() => {
        this.setState({
          resets: this.state.resets + 1,
          stimulusState: StimulusState.RESET,
        });
      }, this.props.timeout * 1000));
    }
  }

  // Method to start the response timer
  private startResponseTimer = () => {
    this.startTime = new Date();
  }

  // Method to update container size and image size stored in the state
  private storeImageDimensions = () => {
    const containerSize: Size = {
      height: this.containerElement.offsetHeight,
      width: this.containerElement.offsetWidth,
    };

    // calc aspect ratio of container and image
    const containerAspectRatio = containerSize.width / containerSize.height;
    const imageAspectRatio = this.state.imageSizeOriginal.width / this.state.imageSizeOriginal.height;

    // calc image dimensions
    let imgWidth = 0;
    let imgHeight = 0;

    // fix the width and calc height based on the width
    if (imageAspectRatio > containerAspectRatio) {
      imgWidth = containerSize.width;
      imgHeight = imgWidth / imageAspectRatio;
    } else {  // fix the height and calc width based on the height
      imgHeight = containerSize.height;
      imgWidth = imgHeight * imageAspectRatio;
    }

    imgWidth = imgWidth || 0; // if image not loaded, it's NaN

    // update in state
    this.setState({
      containerSize,
      imageSizeRender: {
        height: imgHeight,
        width: imgWidth,
      }
    });
  }
}

export default AttractionMobileStimulus;



// WEBPACK FOOTER //
// ./src/components/AttractionMobileStimulus.tsx